import { Checkbox, FormControlLabel, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFindDeliveryOptionsForPartnerQuery } from "@/libs/generated/hooks";

type OptionItem = {
  id: number;
  name: string;
  clientPrice: number;
  dynamicPricing: boolean;
};

type OptionListProps = {
  organizationId: string;
  selectedIds: Set<number>;
  onToggle: (option: OptionItem) => void;
  disabledIds?: Set<number>;
};

export function OptionList({
  organizationId,
  selectedIds,
  onToggle,
  disabledIds,
}: OptionListProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useFindDeliveryOptionsForPartnerQuery(
    { organizationId },
    { enabled: Boolean(organizationId) }
  );

  const options = data?.findDeliveryOptionsForPartner ?? [];

  if (isLoading) {
    return <Typography>Loading options...</Typography>;
  }

  if (options.length === 0) {
    return <Typography>No options available</Typography>;
  }

  return (
    <Stack spacing={1}>
      {options.map((option) => (
        <FormControlLabel
          key={option.id}
          sx={{
            width: "100%",
            "& .MuiFormControlLabel-label": { width: "100%" },
          }}
          control={
            <Checkbox
              checked={selectedIds.has(option.id)}
              disabled={disabledIds?.has(option.id)}
              onChange={() =>
                onToggle({
                  id: option.id,
                  name: option.name,
                  clientPrice: option.dynamicPricing ? 0 : option.clientPrice,
                  dynamicPricing: option.dynamicPricing,
                })
              }
            />
          }
          label={
            <Stack direction="row" justifyContent="space-between" width="100%" sx={{ pr: 2 }}>
              <span style={{ textTransform: "uppercase" }}>{option.name}</span>
              <span>
                {option.dynamicPricing
                  ? t("deliveryOption.pricing.pendingReceipt")
                  : `${option.clientPrice} €`}
              </span>
            </Stack>
          }
        />
      ))}
    </Stack>
  );
}
